import thresholds from "../data/testThresholds.json"
import {
  DIRECTION_IMPROVED,
  DIRECTION_NA,
  DIRECTION_RESOLVED,
  DIRECTION_UNCHANGED,
  DIRECTION_WORSENED,
  FINDING_INCONCLUSIVE,
  FINDING_NEGATIVE,
  FINDING_POSITIVE,
  MEASUREMENT_BINARY,
  MEASUREMENT_NUMERIC,
  MEASUREMENT_ROM,
  MEASUREMENT_TEXT,
  SIGNIFICANCE_BORDERLINE,
  SIGNIFICANCE_NA,
  SIGNIFICANCE_NOISE,
  SIGNIFICANCE_SIGNIFICANT,
} from "../constants/constants"

export const getThresholdForTest = (testKey, measurementType) => {
  const entry = thresholds?.thresholds?.[testKey]
  if (entry) return entry
  const fallback = thresholds?._meta?.defaults?.[measurementType]
  if (fallback) return { ...fallback, measurementType }
  return null
}

const classifySignificance = (absDelta, threshold) => {
  if (!threshold || absDelta == null || Number.isNaN(absDelta)) return SIGNIFICANCE_NA
  if (absDelta < threshold.noise) return SIGNIFICANCE_NOISE
  if (absDelta >= threshold.significant) return SIGNIFICANCE_SIGNIFICANT
  return SIGNIFICANCE_BORDERLINE
}

const naResult = (summary = "") => ({
  delta: null,
  direction: DIRECTION_NA,
  summary,
  clinicalSignificance: SIGNIFICANCE_NA,
})

const compareBinary = (pre, post) => {
  const preResult = pre?.result ?? null
  const postResult = post?.result ?? null
  if (!preResult || !postResult) return naResult("Insufficient data for comparison")
  if (preResult === FINDING_INCONCLUSIVE || postResult === FINDING_INCONCLUSIVE) {
    return naResult(`${preResult} → ${postResult}: inconclusive`)
  }
  if (preResult === FINDING_POSITIVE && postResult === FINDING_NEGATIVE) {
    return {
      delta: null,
      direction: DIRECTION_RESOLVED,
      summary: "positive → negative: resolved",
      clinicalSignificance: SIGNIFICANCE_SIGNIFICANT,
    }
  }
  if (preResult === FINDING_NEGATIVE && postResult === FINDING_POSITIVE) {
    return {
      delta: null,
      direction: DIRECTION_WORSENED,
      summary: "negative → positive: worsened",
      clinicalSignificance: SIGNIFICANCE_SIGNIFICANT,
    }
  }
  return {
    delta: null,
    direction: DIRECTION_UNCHANGED,
    summary: `${preResult} → ${postResult}: unchanged`,
    clinicalSignificance: SIGNIFICANCE_NA,
  }
}

const compareRom = (pre, post, threshold) => {
  const preVal = pre?.romValue?.value
  const postVal = post?.romValue?.value
  if (typeof preVal !== "number" || typeof postVal !== "number") {
    return naResult("ROM values incomplete")
  }
  const delta = postVal - preVal
  const absDelta = Math.abs(delta)
  const significance = classifySignificance(absDelta, threshold)
  const higherIsBetter = threshold?.higherIsBetter !== false
  let direction = DIRECTION_UNCHANGED
  if (significance !== SIGNIFICANCE_NOISE) {
    if (delta > 0) direction = higherIsBetter ? DIRECTION_IMPROVED : DIRECTION_WORSENED
    else if (delta < 0) direction = higherIsBetter ? DIRECTION_WORSENED : DIRECTION_IMPROVED
  }
  const unit = pre?.romValue?.unit ?? post?.romValue?.unit ?? threshold?.unit ?? "°"
  const label = pre?.romValue?.label ?? post?.romValue?.label ?? ""
  const labelPrefix = label ? `${label}: ` : ""
  const directionWord =
    direction === DIRECTION_IMPROVED
      ? `improved by ${absDelta}${unit}`
      : direction === DIRECTION_WORSENED
      ? `reduced by ${absDelta}${unit}`
      : "unchanged"
  return {
    delta,
    direction,
    summary: `${labelPrefix}${preVal}${unit} → ${postVal}${unit} (${directionWord})`,
    clinicalSignificance: significance,
  }
}

const compareNumeric = (pre, post, threshold) => {
  const preVal = pre?.numericValue?.value
  const postVal = post?.numericValue?.value
  if (typeof preVal !== "number" || typeof postVal !== "number") {
    return naResult("Numeric values incomplete")
  }
  const delta = postVal - preVal
  const absDelta = Math.abs(delta)
  const significance = classifySignificance(absDelta, threshold)
  const higherIsBetter = threshold?.higherIsBetter === true
  let direction = DIRECTION_UNCHANGED
  if (significance !== SIGNIFICANCE_NOISE) {
    if (delta > 0) direction = higherIsBetter ? DIRECTION_IMPROVED : DIRECTION_WORSENED
    else if (delta < 0) direction = higherIsBetter ? DIRECTION_WORSENED : DIRECTION_IMPROVED
  }
  const unit = pre?.numericValue?.unit ?? post?.numericValue?.unit ?? threshold?.unit ?? ""
  const directionWord =
    direction === DIRECTION_IMPROVED
      ? `improved by ${absDelta}${unit}`
      : direction === DIRECTION_WORSENED
      ? `worsened by ${absDelta}${unit}`
      : "unchanged"
  return {
    delta,
    direction,
    summary: `${preVal}${unit} → ${postVal}${unit} (${directionWord})`,
    clinicalSignificance: significance,
  }
}

const compareText = (pre, post) => {
  const preText = (pre?.text ?? "").trim()
  const postText = (post?.text ?? "").trim()
  if (!preText && !postText) return naResult("")
  return naResult(`Pre: ${preText || "—"} | Post: ${postText || "—"}`)
}

export const computeComparison = ({ pre, post, measurementType, testKey }) => {
  if (!pre || !post) return naResult("Awaiting post-assessment")
  const threshold = getThresholdForTest(testKey, measurementType)
  switch (measurementType) {
    case MEASUREMENT_BINARY:
      return compareBinary(pre, post)
    case MEASUREMENT_ROM:
      return compareRom(pre, post, threshold)
    case MEASUREMENT_NUMERIC:
      return compareNumeric(pre, post, threshold)
    case MEASUREMENT_TEXT:
      return compareText(pre, post)
    default:
      return naResult("")
  }
}
